/**
 * Flat `answers` state container.
 *
 * Per .claude/skills/farmer-form-engine-frontend/SKILL.md §2: keep the shape of the old
 * global `S` object (get/set/all) so renderer.js / relevance.js / matrix.js can be
 * written against a stable, small interface. What changed vs. the prototype is what's
 * *behind* it: no localStorage here — api-client.js (autosave) and offline/queue.js own
 * persistence. This module is purely an in-memory reactive store plus change tracking so
 * autosave can diff and send only what changed.
 */
export function createState(initialAnswers = {}) {
  const answers = { ...initialAnswers };
  const dirty = new Set(); // keys changed since last markSynced()
  const removed = new Set(); // keys explicitly deleted since last markSynced() (repeat reindex)
  const listeners = new Set();

  function notify(key) {
    listeners.forEach((fn) => {
      try {
        fn(key);
      } catch (err) {
        console.error('state listener error', err);
      }
    });
  }

  const S = {
    get(key) {
      return answers[key] ?? '';
    },
    set(key, value) {
      const v = value === undefined || value === null ? '' : String(value);
      if (answers[key] === v) return;
      answers[key] = v;
      dirty.add(key);
      removed.delete(key);
      notify(key);
    },
    /** Remove a key entirely (used by matrix.js when reindexing repeat rows). */
    delete(key) {
      if (!(key in answers)) return;
      delete answers[key];
      dirty.delete(key);
      removed.add(key);
      notify(key);
    },
    /**
     * Bỏ một khoá mà máy chủ ĐÃ xoá (câu đang ẩn bị cắt khi autosave) — không ghi vào
     * `removed`, vì không còn gì phải báo lại cho máy chủ.
     */
    forget(key) {
      if (!(key in answers)) return;
      delete answers[key];
      dirty.delete(key);
      notify(key);
    },
    has(key) {
      return key in answers;
    },
    all() {
      return { ...answers };
    },
    /** Keys with a non-empty prefix, i.e. all keys starting with `prefix`. */
    keysWithPrefix(prefix) {
      return Object.keys(answers).filter((k) => k.startsWith(prefix));
    },
    /** Replace the whole answers object (e.g. after loading a submission from server). */
    hydrate(next) {
      Object.keys(answers).forEach((k) => delete answers[k]);
      Object.assign(answers, next || {});
      dirty.clear();
      removed.clear();
      notify(null);
    },
    onChange(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    /** Delta since last markSynced(): what to PATCH to the server. */
    getDirty() {
      const out = {};
      dirty.forEach((k) => {
        out[k] = answers[k];
      });
      return out;
    },
    getRemoved() {
      return [...removed];
    },
    /**
     * Clear dirty/removed tracking. Pass `sentKeys` to clear only those dirty keys (used
     * when a hidden/irrelevant branch's changes were deliberately excluded from the
     * outgoing PATCH — see views/submission-form.js — so they stay dirty and get sent
     * later if that branch becomes relevant again, instead of being silently dropped).
     * `removed` always clears fully: a removeRepeatRow reindex is either fully sent or
     * not attempted at all, never partially.
     */
    markSynced(sentKeys) {
      if (sentKeys) sentKeys.forEach((k) => dirty.delete(k));
      else dirty.clear();
      removed.clear();
    },
    isDirty() {
      return dirty.size > 0 || removed.size > 0;
    },
  };

  return S;
}
